// ── V156: 회의 기록 → 테스크 → 오늘의 작업 ─────────────────────────────────────────
// 사장님: "회의를 누르면 회의 기록과, 테스크가 주어지면 그걸 누르면 그 테스크 내용과 담당자 선택,
//          마감일(날짜만)을 선택하게. 회의가 저장되면 오늘의 작업에 그 테스크가 포함되게"
// 데이터 (users/{매장}.meetings, 최근 MEETINGS_MAX건):
//   { id, date 'YYYY-MM-DD', title, notes, tasks: [{ id, text, owner?, due? }], createdAt, createdBy, updatedAt, updatedBy }
// 오늘의 작업 항목: { id, kind: 'text', text, owner?, due?, from: { meetingId, taskId, title, date } }
//   · 회의를 다시 저장하면 이미 들어간 테스크는 내용·담당·마감만 고친다 (체크·순서 그대로)
//   · 회의에서 지운 테스크는 (아직 안 했으면) 오늘의 작업에서도 뺀다
//   · 이미 다 해서 내역으로 간 테스크는 다시 넣지 않는다
import { newWorkId, fmtDay } from './workCalc';
import { applyMeta, fmtDue, TASK_MAX, OWNER_MAX, isDateKey } from './workMeta';

export const MEETINGS_MAX = 60;
export const NOTES_MAX = 4000;
export const TITLE_MAX = 60;

export const newMeetingId = () => 'm' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
export const newTaskId = () => 't' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

/** 저장 전에 정리 — 빈 테스크는 빼고, 칸 길이를 자른다 (undefined 없이) */
export function normMeeting(m = {}, { who = '', now = Date.now() } = {}) {
  const tasks = (Array.isArray(m.tasks) ? m.tasks : [])
    .map(t => {
      const text = String(t?.text || '').replace(/\s+\n/g, '\n').trim().slice(0, TASK_MAX);
      if (!text) return null;
      const out = { id: String(t.id || newTaskId()), text };
      const owner = String(t.owner || '').replace(/\s+/g, ' ').trim().slice(0, OWNER_MAX);
      if (owner) out.owner = owner;
      if (isDateKey(t.due)) out.due = t.due;
      return out;
    })
    .filter(Boolean);
  const out = {
    id: String(m.id || newMeetingId()),
    date: isDateKey(m.date) ? m.date : '',
    title: String(m.title || '').trim().slice(0, TITLE_MAX),
    notes: String(m.notes || '').replace(/\r\n/g, '\n').slice(0, NOTES_MAX).replace(/\s+$/, ''),
    tasks,
    createdAt: Number(m.createdAt) || now,
    updatedAt: now,
  };
  const cb = String(m.createdBy || who || '').slice(0, 30);
  if (cb) out.createdBy = cb;
  if (who) out.updatedBy = String(who).slice(0, 30);
  return out;
}

/** 회의 목록에 넣기 (같은 id는 바꿈) — 날짜 최근 순 */
export function upsertMeeting(list = [], m) {
  const next = [m, ...(list || []).filter(x => x && x.id !== m.id)];
  next.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')) || (Number(b.createdAt) || 0) - (Number(a.createdAt) || 0));
  return next.slice(0, MEETINGS_MAX);
}

const fromOf = (m, t) => {
  const f = { meetingId: m.id, taskId: t.id };
  if (m.title) f.title = m.title;
  if (m.date) f.date = m.date;
  return f;
};

/** 내역에서 이미 끝낸 테스크 id */
function doneTaskIds(history = []) {
  const s = new Set();
  for (const h of history || []) for (const i of (h && h.items) || []) {
    if (i && i.done && i.from && i.from.taskId) s.add(i.from.taskId);
  }
  return s;
}

/**
 * 저장한 회의를 오늘의 작업에 반영
 * @returns { workToday, added, updated, removed }
 */
export function applyMeetingToWork(m, { workToday = { date: '', items: [] }, workChecks = {}, workHistory = [], today = '' } = {}) {
  const items = [...(Array.isArray(workToday.items) ? workToday.items : [])];
  const finished = doneTaskIds(workHistory);
  const taskIds = new Set((m.tasks || []).map(t => t.id));
  let added = 0, updated = 0;
  for (const t of m.tasks || []) {
    const idx = items.findIndex(i => i && i.from && i.from.taskId === t.id);
    if (idx >= 0) {
      const cur = items[idx];
      const next = applyMeta({ ...cur, text: t.text, from: fromOf(m, t) }, { owner: t.owner, due: t.due, note: cur.note });
      if (JSON.stringify(next) !== JSON.stringify(cur)) updated += 1;
      items[idx] = next;
    } else if (!finished.has(t.id)) {
      items.push(applyMeta({ id: newWorkId(), kind: 'text', text: t.text, from: fromOf(m, t) }, { owner: t.owner, due: t.due }));
      added += 1;
    }
  }
  const isDone = (i) => !!(workChecks && workChecks[i.id] && workChecks[i.id].done);
  const kept = items.filter(i => !(i && i.from && i.from.meetingId === m.id && !taskIds.has(i.from.taskId) && !isDone(i)));
  const removed = items.length - kept.length;
  const date = (Array.isArray(workToday.items) && workToday.items.length && workToday.date) ? workToday.date : today;
  return { workToday: { date, items: kept }, added, updated, removed };
}

/** 회의를 지울 때 — 오늘의 작업에 들어간 (아직 안 한) 그 회의 테스크를 뺀다 */
export function removeMeetingFromWork(meetingId, { workToday = { date: '', items: [] }, workChecks = {} } = {}) {
  const items = Array.isArray(workToday.items) ? workToday.items : [];
  const isDone = (i) => !!(workChecks && workChecks[i.id] && workChecks[i.id].done);
  const kept = items.filter(i => !(i && i.from && i.from.meetingId === meetingId && !isDone(i)));
  return { workToday: { date: workToday.date || '', items: kept }, removed: items.length - kept.length };
}

/**
 * 테스크가 어디까지 됐나
 * @returns { state: 'done'|'open'|'missing'|'new', at?, by?, date? }
 *   new: 아직 저장 안 한 테스크 · open: 오늘의 작업에 있음 · done: 체크했거나 내역에 완료로 · missing: 목록에서 빠짐
 */
export function taskStatus(task, { items = [], workChecks = {}, history = [], saved = true } = {}) {
  if (!saved) return { state: 'new' };
  const it = (items || []).find(i => i && i.from && i.from.taskId === task.id);
  if (it) {
    const c = workChecks && workChecks[it.id];
    return c && c.done ? { state: 'done', at: c.at, by: c.by } : { state: 'open' };
  }
  for (const h of history || []) for (const i of (h && h.items) || []) {
    if (i && i.done && i.from && i.from.taskId === task.id) return { state: 'done', at: i.at, by: i.by, date: h.date };
  }
  return { state: 'missing' };
}

// ── 위챗으로 보낼 회의록 글 ── (mode 'bi' 한국어+중국어 · 'zh' · 'ko'. 적은 글은 그대로)
const T = (mode, ko, zh) => (mode === 'zh' ? zh : mode === 'ko' ? ko : (ko === zh ? ko : `${ko}·${zh}`));
export function buildMeetingText(m, { mode = 'bi', today = '', storeName = '', statusOf = null, withStatus = true } = {}) {
  const L = [];
  L.push(`🗓 ${T(mode, '회의록', '会议记录').replace('·', ' ')}`);
  const dayKo = fmtDay(m.date, 'ko'); const dayZh = fmtDay(m.date, 'zh');
  const day = mode === 'zh' ? dayZh : mode === 'ko' ? dayKo : (m.date ? `${dayKo} · ${dayZh}` : '');
  const head = [day, String(storeName || '').trim()].filter(Boolean).join(' · ');
  if (head) L.push(head);
  if (m.title) L.push(`【${m.title}】`);
  const notes = String(m.notes || '').trim();
  if (notes) { L.push('', `📝 ${T(mode, '회의 내용', '会议内容')}`, notes); }
  const tasks = m.tasks || [];
  if (tasks.length) {
    L.push('', `📌 ${T(mode, '테스크', '任务')} (${tasks.length})`);
    tasks.forEach((t, k) => {
      const st = withStatus && statusOf ? statusOf(t) : null;
      const box = st ? (st.state === 'done' ? '✅ ' : '⬜ ') : '';
      L.push(`${k + 1}. ${box}${t.text}`);
      const meta = [];
      if (t.owner) meta.push(`👤 ${T(mode, '담당', '负责')} ${t.owner}`);
      const due = fmtDue(t, mode, today);
      if (due) meta.push(`⏰ ${T(mode, '마감', '截止')} ${due}`);
      if (meta.length) L.push(`   ${meta.join(' · ')}`);
    });
  }
  return L.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

/** 위챗 한 번에 보내기 좋은 길이로 나누기 — 줄 단위 (한 줄이 너무 길면 그 줄을 자른다) */
export function splitForWechat(text, max = 1800) {
  const s = String(text || '');
  if (s.length <= max) return [s];
  const parts = [];
  let cur = '';
  const push = () => { if (cur.trim()) parts.push(cur.replace(/\n+$/, '')); cur = ''; };
  for (let line of s.split('\n')) {
    while (line.length > max) { push(); parts.push(line.slice(0, max)); line = line.slice(max); }
    if (cur.length + line.length + 1 > max) push();
    cur += (cur ? '\n' : '') + line;
  }
  push();
  return parts.length > 1 ? parts.map((p, i) => `(${i + 1}/${parts.length})\n${p}`) : parts;
}
